/**
 * @file SubmitButtonComponent.tsx
 * @description
 * -----------------------------------------------------
 * Componente de submit button
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2024-03-03
 * @modified 2026-02-27
 * @author Correcta
 * @author Sergio Manjón
 * @copyright © Correcta
 * @license Private
 * */

"use client";
import React, { useEffect } from "react";
import ButtonComponent from "@/components/ui/ButtonComponent";
import { useFormStatus } from "react-dom";
import { useTranslations } from "use-intl";
import { TYPE_SUBMIT } from "@/constants/button";
import { useLoadingOverlay } from "@/context/LoadingOverlayProvider";

interface SubmitButtonProps<TFormData = Record<string, unknown>>
  extends React.PropsWithChildren {
  formData?: TFormData;
  buttonLabel: string;
  className?: string;
  status?: string;
  iconPosition?: "left" | "right";
  isButtonEnabled?: boolean;
}

const SubmitButtonComponent = <TFormData = Record<string, unknown>,>({
  formData,
  buttonLabel,
  className,
  status = "primary",
  children,
  iconPosition = "right",
  isButtonEnabled,
}: SubmitButtonProps<TFormData>) => {
  const { pending } = useFormStatus();
  const t = useTranslations("Buttons");
  const { show, hide } = useLoadingOverlay();

  useEffect(() => {
    if (pending) {
      show("data");
      return;
    }

    hide("data");
  }, [hide, pending, show]);

  const isEmpty =
    !formData ||
    Object.values(formData).some((value) => {
      if (typeof value === "string") return value.trim() === "";
      return typeof value === "undefined" || value === null;
    });

  return (
    <ButtonComponent
      status={status}
      className={className}
      type={TYPE_SUBMIT}
      disabled={pending || (isEmpty && !isButtonEnabled)}
      iconPosition={iconPosition}
      label={pending ? "" : buttonLabel}
      ariaLabel={buttonLabel ? t(buttonLabel) : t("buttonSubmit")}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          if (pending || (isEmpty && !isButtonEnabled)) return;
          e.preventDefault();
          e.currentTarget.form?.requestSubmit();
        }
      }}
    >
      {pending ? t("buttonLoading") : (children ?? null)}
    </ButtonComponent>
  );
};

export default SubmitButtonComponent;
